import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, Text } from '@fragments';
import { IconAlertCircle, IconCheck, IconCircle, IconCircleDotted, IconCircleX, IconRefresh } from '@tabler/icons-react';
import { errorKey } from '../../../../data/studioApi';
import StudioAi from '../../../../components/StudioAi';
import StepTicker from './components/StepTicker';
import useRunProgress, { phaseKey } from './hooks/useRunProgress';
import { isArtProject } from '../../../../enums/formats';
import memoStudio from '../../../../utils/memoStudio';
import './styles.scss';

const STATUS_ICONS = { succeeded: IconCheck, waiting_user: IconCircleDotted, failed: IconAlertCircle, cancelled: IconCircleX };
const reveal = { initial: { height: 0, opacity: 0 }, animate: { height: 'auto', opacity: 1 }, exit: { height: 0, opacity: 0 }, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] } };

function Steps({ id, steps, done, t, art }) {
  return <motion.ol id={id} className="studio-run__steps" aria-label={t('runSteps')} {...reveal}>
    {steps.map(phase => <li key={phase}><Text.caption contrast={done ? 7 : 5} left={done ? <IconCheck /> : <IconCircle />}>{t(phaseKey(phase, art))}</Text.caption></li>)}
  </motion.ol>;
}

// Only the current step shows; clicking it reveals the steps before and after it.
// While the run works its mark is the AI orb, in the column of the messages' orbs.
function RunProgress({ studio: s }) {
  const { t, run } = s;
  const art = isArtProject(s.current);
  const { open, toggle, listId, working, answered, labelKey, done, upcoming } = useRunProgress(run, art);
  const StatusIcon = STATUS_ICONS[run.status];
  if (answered) return null;
  const controls = [done.length && `${listId}-done`, upcoming.length && `${listId}-next`].filter(Boolean).join(' ');
  return <div className="studio-run">
    <AnimatePresence initial={false}>{open && done.length > 0 && <Steps key="done" id={`${listId}-done`} steps={done} done t={t} art={art} />}</AnimatePresence>
    <button type="button" className="studio-run__current" aria-expanded={open} aria-controls={open && controls ? controls : undefined} onClick={toggle}>
      <span className="studio-run__mark">{working ? <StudioAi size={26} active /> : <StatusIcon className="studio-run__icon" aria-hidden="true" />}</span>
      <StepTicker id={labelKey} text={t(labelKey)} loading={working} />
    </button>
    <AnimatePresence initial={false}>{open && upcoming.length > 0 && <Steps key="next" id={`${listId}-next`} steps={upcoming} t={t} art={art} />}</AnimatePresence>
    {['failed','cancelled'].includes(run.status) && <div className="studio-fields"><Text.caption contrast={7}>{t(run.status === 'cancelled' ? 'cancelled' : `error_${errorKey(run.error)}`)}</Text.caption><Button color="inverse" size="small" startIcon={<IconRefresh />} onClick={s.retryRun}>{t('retry')}</Button></div>}
  </div>;
}

// Every `studio` field the progress reads: it renders again only when the run moves.
export default memoStudio(RunProgress, ['t', 'run', 'retryRun', 'current']);
